import { useEffect, useMemo, useState } from "react";
import { Plus, X, KeyRound, Trash2, ShieldCheck, AlertTriangle, Save, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { USERS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

// 2026-11 · "uploader" (biometric upload only — e.g. Santus) existed in the
// backend but couldn't be chosen here. Plain-English help for each role.
const ROLES = [
    { value: "admin", label: "Admin", help: "Everything: pay runs, approve, employees, settings, users." },
    { value: "operator", label: "Operator", help: "View only: can see pay runs, employees and reports, but can't change anything." },
    { value: "uploader", label: "Uploader", help: "Can only upload the biometric file for review — no rates, pay or bank details." },
];

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    // Per-row role drafts. { userId: "admin" | "operator" }
    const [roleDraft, setRoleDraft] = useState({});
    // Pending role change awaiting the confirm modal.
    // { user, from: "admin"|"operator", to: "admin"|"operator" }
    const [pendingRoleChange, setPendingRoleChange] = useState(null);
    const [savingRole, setSavingRole] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
            setRoleDraft({});  // any pending drafts stale after a refresh
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Last-admin protection is enforced server-side; the UI mirrors it so the
    // operator sees the constraint before they act. `isLastActiveAdmin(u)` =
    // u is an active admin AND removing u would leave zero active admins.
    const activeAdminCount = useMemo(
        () => users.filter((u) => u.role === "admin" && u.active).length,
        [users],
    );
    const isLastActiveAdmin = (u) =>
        u.role === "admin" && u.active && activeAdminCount <= 1;

    const deactivate = async (u) => {
        if (!window.confirm(`Deactivate ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deactivated");
            load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    // 2026-11 · a deactivated user could never be brought back from the UI.
    const reactivate = async (u) => {
        if (!window.confirm(`Reactivate ${u.email}? They will be able to sign in again with their existing password.`)) return;
        try {
            await api.patch(`/users/${u.id}`, { active: true });
            toast.success(`${u.email} reactivated`);
            load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const pendingRoleFor = (u) => roleDraft[u.id] ?? u.role;
    const hasRoleChange = (u) => pendingRoleFor(u) !== u.role;

    const beginRoleChange = (u) => {
        const to = pendingRoleFor(u);
        setPendingRoleChange({ user: u, from: u.role, to });
    };

    const confirmRoleChange = async () => {
        if (!pendingRoleChange) return;
        const { user, to } = pendingRoleChange;
        setSavingRole(true);
        try {
            await api.patch(`/users/${user.id}`, { role: to });
            toast.success(`${user.email} is now ${to}`);
            setPendingRoleChange(null);
            load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Role change failed");
        } finally {
            setSavingRole(false);
        }
    };

    return (
        <div data-testid={USERS.page} className="p-10 max-w-[1200px]">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                        Settings · Users
                    </div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        User management
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        Admin-only. Add users, edit names and emails, change roles, reset passwords, deactivate and reactivate accounts.
                    </p>
                </div>
                <button
                    data-testid={USERS.addBtn}
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                    style={{ background: "var(--kp-red)" }}
                >
                    <Plus size={16} strokeWidth={2.2} /> New user
                </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-[3px]">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                            <th className="px-5 py-3">Email</th>
                            <th className="px-5 py-3">Name</th>
                            <th className="px-5 py-3">Role</th>
                            <th className="px-5 py-3">Status</th>
                            <th className="px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-400 text-xs">Loading…</td></tr>
                        )}
                        {!loading && users.map((u) => {
                            const draftRole = pendingRoleFor(u);
                            const dirty = hasRoleChange(u);
                            const lastAdmin = isLastActiveAdmin(u);
                            // The role select is disabled when:
                            //   - user is the last active admin (can't demote — server would 400)
                            //   - user is inactive (deactivated accounts don't need role edits)
                            const roleSelectDisabled = lastAdmin || !u.active;
                            const deactivateDisabled = lastAdmin || u.id === me?.id || !u.active;
                            return (
                                <tr key={u.id} data-testid={USERS.row(u.id)} className="border-t border-neutral-100 hover:bg-neutral-50">
                                    <td className="px-5 py-3 text-[color:var(--kp-charcoal)] font-medium">{u.email}</td>
                                    <td className="px-5 py-3 text-neutral-600">{u.name || "—"}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-2">
                                            <select
                                                data-testid={USERS.roleSelect(u.id)}
                                                value={draftRole}
                                                disabled={roleSelectDisabled}
                                                onChange={(e) => setRoleDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                                                title={
                                                    lastAdmin
                                                        ? "Last active admin — promote another user to admin before demoting this one."
                                                        : (!u.active ? "Reactivate the account before changing role." : "Change role")
                                                }
                                                className="kp-focus text-[12px] font-display font-semibold uppercase tracking-[0.1em] border border-neutral-300 rounded-[3px] px-2 py-1 bg-white disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed"
                                            >
                                                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                            </select>
                                            {dirty && (
                                                <button
                                                    data-testid={USERS.roleSaveBtn(u.id)}
                                                    onClick={() => beginRoleChange(u)}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-display font-semibold uppercase tracking-[0.14em] text-white rounded-[3px]"
                                                    style={{ background: "var(--kp-red)" }}
                                                    title={`Save role change: ${u.role} → ${draftRole}`}
                                                >
                                                    <Save size={11} /> Save role
                                                </button>
                                            )}
                                            {lastAdmin && (
                                                <span
                                                    data-testid={USERS.lastAdminBadge(u.id)}
                                                    title="Last active admin — protected against lockout."
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                                    style={{ background: "var(--kp-charcoal)", color: "#fff" }}
                                                >
                                                    <ShieldCheck size={9} /> Last admin
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-5 py-3 text-neutral-600">
                                        {u.active ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs">
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--kp-success)" }} />
                                                Active
                                            </span>
                                        ) : (
                                            <span className="text-neutral-400 text-xs">Inactive</span>
                                        )}
                                        {u.active && u.password_is_seed && (
                                            <span
                                                data-testid={`users-temp-pw-${u.id}`}
                                                title="Still on a temporary password — they'll be prompted to change it when they sign in."
                                                className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] font-display font-semibold uppercase tracking-[0.12em] rounded-[2px]"
                                                style={{ background: "#FEF6E6", color: "#8A5A00" }}
                                            >
                                                Temp password
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <button
                                            data-testid={`users-edit-${u.id}`}
                                            onClick={() => setEditTarget(u)}
                                            className="inline-flex items-center gap-1 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-[color:var(--kp-red)] mr-4"
                                        >
                                            <Pencil size={12} /> Edit
                                        </button>
                                        <button
                                            data-testid={USERS.resetBtn(u.id)}
                                            onClick={() => setResetTarget(u)}
                                            className="inline-flex items-center gap-1 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-[color:var(--kp-red)] mr-4"
                                        >
                                            <KeyRound size={12} /> Reset
                                        </button>
                                        {!u.active ? (
                                        <button
                                            data-testid={`users-reactivate-${u.id}`}
                                            onClick={() => reactivate(u)}
                                            title="Let this user sign in again."
                                            className="inline-flex items-center gap-1 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-[color:var(--kp-red)] hover:text-black"
                                        >
                                            <RotateCcw size={12} /> Reactivate
                                        </button>
                                        ) : (
                                        <button
                                            data-testid={USERS.deleteBtn(u.id)}
                                            onClick={() => !deactivateDisabled && deactivate(u)}
                                            disabled={deactivateDisabled}
                                            title={
                                                lastAdmin
                                                    ? "Last active admin — protected against lockout."
                                                    : (u.id === me?.id ? "You can't deactivate yourself."
                                                        : (!u.active ? "Already inactive." : "Deactivate this account."))
                                            }
                                            className="inline-flex items-center gap-1 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-[color:var(--kp-red)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-neutral-600"
                                        >
                                            <Trash2 size={12} /> Deactivate
                                        </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreate && (
                <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
            )}
            {editTarget && (
                <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />
            )}
            {resetTarget && (
                <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onDone={() => setResetTarget(null)} />
            )}
            {pendingRoleChange && (
                <RoleChangeConfirmModal
                    change={pendingRoleChange}
                    saving={savingRole}
                    onCancel={() => !savingRole && setPendingRoleChange(null)}
                    onConfirm={confirmRoleChange}
                />
            )}
        </div>
    );
}

function ModalShell({ title, subtitle, onClose, children, testid }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" data-testid={testid}>
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white border border-neutral-200 shadow-xl w-full max-w-md rounded-[3px]">
                <div className="px-5 py-4 border-b border-neutral-200 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">{title}</div>
                        <div className="font-display font-bold text-lg text-[color:var(--kp-charcoal)] mt-0.5">{subtitle}</div>
                    </div>
                    <button onClick={onClose} className="p-1 text-neutral-500 hover:text-black"><X size={16} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function RoleChangeConfirmModal({ change, saving, onCancel, onConfirm }) {
    const { user, from, to } = change;
    return (
        <ModalShell
            title="Change role"
            subtitle={user.email}
            onClose={onCancel}
            testid={USERS.roleConfirmModal}
        >
            <div className="px-5 py-5 space-y-4">
                <div className="text-[13px] text-neutral-700">
                    You are changing this user's role:
                </div>
                <div className="flex items-center gap-3 font-display font-bold text-lg">
                    <span className="px-2 py-1 text-[11px] uppercase tracking-[0.14em] rounded-[2px]"
                          style={{ background: "#F0F0F1", color: "var(--kp-charcoal)" }}>
                        {from}
                    </span>
                    <span className="text-neutral-400">→</span>
                    <span className="px-2 py-1 text-[11px] uppercase tracking-[0.14em] text-white rounded-[2px]"
                          style={{ background: "var(--kp-red)" }}>
                        {to}
                    </span>
                </div>
                <div
                    className="flex items-start gap-3 px-3 py-3 border-l-2 rounded-[3px]"
                    style={{ background: "#FEF6E6", borderLeftColor: "#C88A17", color: "#5A3600" }}
                >
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: "#C88A17" }} />
                    <div className="text-[12.5px] leading-relaxed">
                        <strong>They must sign out and back in.</strong> The JWT is stateless and still
                        says <code className="font-mono-money">"{from}"</code> until they get a fresh
                        token at their next login — until then, their current session keeps the old
                        permissions.
                    </div>
                </div>
            </div>
            <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-3">
                <button
                    data-testid={USERS.roleCancelBtn}
                    onClick={onCancel}
                    disabled={saving}
                    className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600"
                >
                    Cancel
                </button>
                <button
                    data-testid={USERS.roleConfirmBtn}
                    onClick={onConfirm}
                    disabled={saving}
                    className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-70"
                    style={{ background: "var(--kp-red)" }}
                >
                    {saving ? "Saving…" : `Change to ${to}`}
                </button>
            </div>
        </ModalShell>
    );
}

function CreateUserModal({ onClose, onCreated }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("operator");
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (password.length < 8) { toast.error("Temporary password must be at least 8 characters"); return; }
        setSaving(true);
        try {
            await api.post("/users", { email: email.trim(), name: name.trim(), role, password, active: true });
            toast.success("User created");
            onCreated();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed");
        } finally { setSaving(false); }
    };

    return (
        <ModalShell title="New user" subtitle="Create account" onClose={onClose}>
            <div className="px-5 py-5 space-y-4">
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">Email</span>
                    <input data-testid={USERS.modalEmail} value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]" />
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">Name</span>
                    <input data-testid={USERS.modalName} value={name} onChange={(e) => setName(e.target.value)} className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]" />
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">Role</span>
                    <select data-testid={USERS.modalRole} value={role} onChange={(e) => setRole(e.target.value)} className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]">
                        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <span className="block mt-1 text-[11px] text-neutral-500">{ROLES.find((r) => r.value === role)?.help}</span>
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">Temporary password (min 8 characters)</span>
                    <input data-testid={USERS.modalPassword} value={password} onChange={(e) => setPassword(e.target.value)} type="text" className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white font-mono-money rounded-[3px]" />
                </label>
            </div>
            <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-3">
                <button onClick={onClose} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600">Cancel</button>
                <button data-testid={USERS.modalSaveBtn} onClick={save} disabled={saving || !email || !password} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-70" style={{ background: "var(--kp-red)" }}>
                    {saving ? "Creating…" : "Create user"}
                </button>
            </div>
        </ModalShell>
    );
}

function ResetPasswordModal({ user, onClose, onDone }) {
    const [pw, setPw] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        setSaving(true);
        try {
            await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
            toast.success("Password reset");
            onDone();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <ModalShell title="Reset password" subtitle={user.email} onClose={onClose}>
            <div className="px-5 py-5">
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">New temporary password (min 8 chars)</span>
                    <input data-testid="users-reset-input" value={pw} onChange={(e) => setPw(e.target.value)} type="text" className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white font-mono-money rounded-[3px]" />
                </label>
                <p className="mt-3 text-[12px] text-neutral-600">
                    Give it to them by text or phone, not email. They'll be prompted to choose their own password when they sign in.
                </p>
            </div>
            <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-3">
                <button onClick={onClose} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600">Cancel</button>
                <button data-testid="users-reset-save" onClick={save} disabled={saving} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-70" style={{ background: "var(--kp-red)" }}>
                    {saving ? "Saving…" : "Reset password"}
                </button>
            </div>
        </ModalShell>
    );
}


function EditUserModal({ user, onClose, onSaved }) {
    const [email, setEmail] = useState(user.email || "");
    const [name, setName] = useState(user.name || "");
    const [saving, setSaving] = useState(false);
    const changed = email.trim().toLowerCase() !== (user.email || "") || name.trim() !== (user.name || "");

    const save = async () => {
        const patch = {};
        if (email.trim().toLowerCase() !== (user.email || "")) patch.email = email.trim();
        if (name.trim() !== (user.name || "")) patch.name = name.trim();
        setSaving(true);
        try {
            await api.patch(`/users/${user.id}`, patch);
            toast.success("User updated");
            onSaved();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Update failed");
        } finally { setSaving(false); }
    };

    return (
        <ModalShell title="Edit user" subtitle={user.email || "(no email)"} onClose={onClose} testid="users-edit-modal">
            <div className="px-5 py-5 space-y-4">
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">Name</span>
                    <input data-testid="users-edit-name" value={name} onChange={(e) => setName(e.target.value)} className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]" />
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display">Email (used to sign in)</span>
                    <input data-testid="users-edit-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]" />
                </label>
                <p className="text-[12px] text-neutral-600">Changing the email changes what they type to sign in. Their password stays the same.</p>
            </div>
            <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-3">
                <button onClick={onClose} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600">Cancel</button>
                <button data-testid="users-edit-save" onClick={save} disabled={saving || !changed || !email.trim()} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-50" style={{ background: "var(--kp-red)" }}>
                    {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </ModalShell>
    );
}
